import type { IPaymentSelected } from "../interfaces/payment";
import { useStore } from "../store/globalState";
import {
  useComposeStore,
  type FundingInstrument,
} from "../store/useComposeStore";
import type { FundingCategory, FundingMethod } from "../utils/sendCatalog";

/** Query flag that turns a flow's Transfer screen into its first-send variant. */
export const GALLERY_FIRST_SEND_PARAM = "galleryFirstSend";

export type FirstSendCopy = "default" | "long";

/** Mirrors the backend default (`first_send_warning` template). */
export const FIRST_SEND_THRESHOLD = { amount: 500, currency: "USD" } as const;

/** Send amount seeded by the variant: strictly above the threshold. */
export const FIRST_SEND_SEED_AMOUNT = "750";

export const firstSendRoute = (
  route: string,
  copy: FirstSendCopy = "default",
): string => {
  const [path, query] = route.split("?");
  const params = new URLSearchParams(query);
  params.set(GALLERY_FIRST_SEND_PARAM, copy === "long" ? "long" : "1");
  return `${path}?${params.toString()}`;
};

export const parseFirstSendCopy = (
  value: string | null,
): FirstSendCopy | null => {
  if (!value) return null;
  return value === "long" ? "long" : "default";
};

/**
 * Seeds an above-threshold send amount plus a debit funding source, and
 * returns a restore so the plain Transfer screen never inherits the variant
 * (amount, payment and compose are global stores).
 */
export const applyFirstSendSeed = ({
  payment,
  compose,
}: {
  payment?: IPaymentSelected;
  compose?: { funding: FundingMethod; instrument: FundingInstrument | null };
}): (() => void) => {
  const store = useStore.getState();
  const composeStore = useComposeStore.getState();
  const previous = {
    amountState: store.amountState,
    paymentState: store.paymentState,
    funding: composeStore.funding,
    fundingInstrument: composeStore.fundingInstrument,
  };

  store.setAmountState({ amount: FIRST_SEND_SEED_AMOUNT, anchor: "send" });
  if (payment) store.setPaymentState({ payment });
  if (compose) {
    composeStore.setFunding(compose.funding);
    composeStore.setFundingInstrument(compose.instrument);
  }

  return () => {
    store.setAmountState(previous.amountState);
    if (payment) store.setPaymentState(previous.paymentState);
    if (compose) {
      composeStore.setFunding(previous.funding);
      composeStore.setFundingInstrument(previous.fundingInstrument);
    }
  };
};

/**
 * Same presentation rule as the widgets: source currency must match, the
 * amount must be strictly above the threshold, and only account/card debits
 * qualify (cash or no funding yet hides it).
 */
export const isFirstSendWarningVisible = ({
  sendAmount,
  sourceCurrency,
  fundingCategory,
}: {
  sendAmount: number | null | undefined;
  sourceCurrency: string | null | undefined;
  fundingCategory: FundingCategory | null | undefined;
}): boolean =>
  sendAmount != null &&
  Number.isFinite(sendAmount) &&
  sendAmount > FIRST_SEND_THRESHOLD.amount &&
  sourceCurrency === FIRST_SEND_THRESHOLD.currency &&
  (fundingCategory === "CARD" || fundingCategory === "ACCOUNT");
